import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Radio, Table } from 'antd';
import DateRangePicker, {
  filterFactory,
  filter_this_month,
  variant_dropdown,
} from '../../components/filters/DateRangePicker';
import useApiClient from '../../hooks/api/useApiClient';
import useHelpers from '../../hooks/context/useHelpers';
import useDateRangeFilter from '../../hooks/utility/useDateRangeFilter';

const salesVolumeOptions = {
  threshold_0: '0',
  threshold_1_699: '1_699',
  threshold_700_2499: '700_2499',
  threshold_2500: '2500+',
  all: 'all',
};

const sorterDirections = {
  ascend: 'ascend',
  descend: 'descend',
};

const sorterFields = {
  total_orders: 'total_orders',
  total_sales: 'total_sales',
};

export default function PerformanceTab({ selectedManagers, renderManagerSelect }) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Views:CustomerBase:performance_tab' });

  const { CustomerBaseStatsManager } = useApiClient();

  const { formatCurrency } = useHelpers();

  const [loading, setLoading] = useState(true);
  const [dateRangeFilter, setDateRangeFilter] = useDateRangeFilter(filterFactory(filter_this_month));
  const [data, setData] = useState(null);
  const [sorter, setSorter] = useState({
    field: sorterFields.total_sales,
    order: sorterDirections.descend,
  });
  const [salesVolume, setSalesVolume] = useState(salesVolumeOptions.all);

  useEffect(() => {
    fetchData(data?.current_page || 1, sorter);
  }, [selectedManagers, dateRangeFilter, salesVolume]);

  const fetchData = async (page, sorter) => {
    setLoading(true);
    const { data, success } = await CustomerBaseStatsManager.getPerformanceStats({
      include_previous_month_sales: true,
      start_date: dateRangeFilter.start_date,
      end_date: dateRangeFilter.end_date,
      page,
      sort_field: sorter.field,
      sort_order: sorter.order === sorterDirections.ascend ? 'asc' : 'desc',
      managers: selectedManagers,
      ...getSalesVolumeRequestData(),
    });
    setLoading(false);

    if (!success) {
      return toast.error(t('toasts.failed_to_load_data'));
    }

    setData(data);
  };

  const getSalesVolumeRequestData = () => {
    if (salesVolume === salesVolumeOptions.threshold_0) {
      return { sales_volume_min: 0, sales_volume_max: 0 };
    }

    if (salesVolume === salesVolumeOptions.threshold_1_699) {
      return { sales_volume_min: 1, sales_volume_max: 69900 };
    }

    if (salesVolume === salesVolumeOptions.threshold_700_2499) {
      return { sales_volume_min: 70000, sales_volume_max: 249900 };
    }

    if (salesVolume === salesVolumeOptions.threshold_2500) {
      return { sales_volume_min: 250000, sales_volume_max: null };
    }

    return { sales_volume_min: null, sales_volume_max: null };
  };

  const handleTableChange = (pagination, filters, pendingSorter) => {
    const newSorter = {
      field: pendingSorter.field || sorter.field,
      order:
        pendingSorter.order ||
        (sorter.order === sorterDirections.descend ? sorterDirections.ascend : sorterDirections.descend),
    };

    setSorter(newSorter);

    fetchData(pagination.current, newSorter);
  };

  const columns = [
    {
      title: t('table_columns.name'),
      key: 'name',
      dataIndex: 'name',
    },
    {
      title: t('table_columns.sales_manager'),
      key: 'sales_manager_full_name',
      dataIndex: 'sales_manager_full_name',
    },
    {
      title: t('table_columns.orders'),
      key: 'total_orders',
      dataIndex: 'total_orders',
      sorter: true,
      sortOrder: sorter.field === sorterFields.total_orders ? sorter.order : null,
    },
    {
      title: t('table_columns.sales'),
      key: 'total_sales',
      dataIndex: 'total_sales',
      sorter: true,
      sortOrder: sorter.field === sorterFields.total_sales ? sorter.order : null,
      render: (value) => formatCurrency(value),
    },
    {
      title: t('table_columns.previous_month_sales'),
      key: 'previous_month_total_sales',
      dataIndex: 'previous_month_total_sales',
      render: (value) => formatCurrency(value),
    },
  ];

  return (
    <div className="mt-4 bg-background-inkWhite-white_0 shadow rounded-lg">
      <div className="p-md border-b border-brand-inkGrey-grey_2">
        <div className="flex flex-col lg:flex-row items-center justify-between gap-4">
          <DateRangePicker
            variant={variant_dropdown}
            defaultFilter={filter_this_month}
            initialValue={dateRangeFilter}
            onChange={setDateRangeFilter}
          />
          <div className="flex flex-row items-center">
            <span className="font-semibold mr-sm">{t('labels.sales_volume')}</span>
            <Radio.Group defaultValue={salesVolumeOptions.all} onChange={(e) => setSalesVolume(e.target.value)}>
              <Radio.Button value={salesVolumeOptions.threshold_0}>0</Radio.Button>
              <Radio.Button value={salesVolumeOptions.threshold_1_699}>&lt; 700</Radio.Button>
              <Radio.Button value={salesVolumeOptions.threshold_700_2499}>700 - 2499</Radio.Button>
              <Radio.Button value={salesVolumeOptions.threshold_2500}>&gt;= 2500</Radio.Button>
              <Radio.Button value={salesVolumeOptions.all}>{t('labels.all')}</Radio.Button>
            </Radio.Group>
          </div>
        </div>
        {renderManagerSelect()}
      </div>
      <Table
        dataSource={data?.data}
        columns={columns}
        pagination={{
          current: data?.current_page,
          total: data?.total,
          pageSize: data?.per_page,
          className: 'pr-[5px]',
        }}
        sorter={sorter}
        loading={loading}
        className="rounded-none shadow-card bg-white overflow-hidden"
        onChange={handleTableChange}
        rowKey={(item) => item.id}
        locale={{
          emptyText: t('labels.no_data'),
          triggerDesc: t('labels.click_to_sort'),
          triggerAsc: t('labels.click_to_sort'),
          cancelSort: t('labels.click_to_sort'),
        }}
      />
    </div>
  );
}
